import { OppButton, OppTextarea } from './ui/control';
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logError } from '../utils/errorHandler';
import { useComments, useCreateComment } from '../hooks/useOpp';
import { createCommentSchema } from '../lib/schemas';
import { useSharedAuth } from '@litopia/shared';
import { useCopy } from '../copy';
import { formatDistanceToNow } from 'date-fns';
import { SemanticState } from './semantic/SemanticState';
import { SemanticFeedback } from './semantic/SemanticFeedback';

interface CommentSectionProps {
  targetId: string;
  targetType: string;
}

export default function CommentSection({ targetId, targetType }: CommentSectionProps) {
  const { user } = useSharedAuth();
  const { t } = useCopy();
  const [text, setText] = useState('');
  const { data: comments, isLoading, isError, refetch } = useComments(targetId, targetType);
  const createComment = useCreateComment();
  const [sentFeedback, setSentFeedback] = useState(false);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || !user) return;
    const result = createCommentSchema.safeParse({
      content: trimmed,
      target_id: targetId,
      target_type: targetType,
    });
    if (!result.success) {
      toast.error(result.error.errors[0].message);
      return;
    }
    createComment.mutate(
      { target_id: targetId, target_type: targetType, content: trimmed },
      {
        onSuccess: () => { setText(''); setSentFeedback(true); window.setTimeout(() => setSentFeedback(false), 3000); },
        onError: (err) => {
          logError(err, 'Create comment');
          toast.error(err instanceof Error ? err.message : '评论发送失败');
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
        <span className="text-sm font-semibold text-[hsl(var(--opp-ink))]">
          {t('comment.title')} {comments && comments.length > 0 ? `(${comments.length})` : ''}
        </span>
      </div>

      {/* Comment list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="h-8 w-8 shrink-0 rounded-full bg-[hsl(var(--opp-accent-soft))]" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-24 rounded bg-[hsl(var(--opp-accent-soft))]" />
                <div className="h-3 w-full rounded bg-[hsl(var(--opp-accent-soft))]" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <SemanticState compact kind="error" illustration="state-error-network" title="评论暂时无法加载" description="请检查网络后重试。" action={<OppButton onClick={() => refetch()} variant="primary" className="min-h-11 rounded-full px-4 text-xs font-semibold">重试</OppButton>} />
      ) : comments && comments.length > 0 ? (
        <div className="space-y-3">
          {comments.map((comment) => {
            const profile = comment.public_profiles as { username?: string; avatar_url?: string } | null;
            const displayName = comment.is_anonymous
              ? t('comment.anonymous')
              : (profile?.username ?? comment.user_id?.slice(0, 8) ?? t('comment.anonymous'));
            const initial = displayName.slice(0, 1).toUpperCase();
            const date = comment.created_at
              ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })
              : '';
            return (
              <div key={comment.id} className="flex gap-3">
                {profile?.avatar_url && !comment.is_anonymous ? (
                  <img src={profile.avatar_url} alt={displayName} className="h-8 w-8 shrink-0 rounded-full object-cover" />
                ) : (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--opp-accent-soft))] text-xs font-bold text-[hsl(var(--opp-ink))]">
                    {initial}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-[hsl(var(--opp-ink))]">
                      {displayName}
                    </span>
                    <span className="text-caption text-[hsl(var(--opp-muted))]">{date}</span>
                  </div>
                  <p className="mt-0.5 text-sm leading-relaxed text-[hsl(var(--opp-accent-strong))]">{DOMPurify.sanitize(comment.content || '', { ALLOWED_TAGS: [] })}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <SemanticState compact illustration="empty-comments" kind="empty" title={t('comment.empty')} />
      )}

      {/* Input */}
      {user ? (
        <div className="flex gap-2 rounded-xl bg-[hsl(var(--opp-accent-soft))] p-2">
          <OppTextarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('comment.placeholder')}
            rows={2}
            className="flex-1 resize-none rounded-lg bg-[hsl(var(--opp-surface-raised))] px-3 py-2 text-sm text-[hsl(var(--opp-ink))] placeholder:text-[hsl(var(--opp-muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent)/.20)]"
          />
          <OppButton
            onClick={handleSend}
            disabled={!text.trim() || createComment.isPending}
            aria-label="Send comment"
            variant="primary"
            size="icon"
            className="self-end shrink-0 rounded-lg disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </OppButton>
        </div>
      ) : (
        <p className="text-center text-xs text-[hsl(var(--opp-muted))]">{t('comment.loginRequired')}</p>
      )}
      {sentFeedback && <SemanticFeedback kind="success" title="评论已发布" className="mt-2">你的回应已经显示在评论区。</SemanticFeedback>}
    </div>
  );
}
